import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { TimezoneSchema, validTimezones, type Timezone } from '@campai/lib-common/entities'

import { cn } from '@/components/new/lib'

import { ValueSelect } from './ValueSelect'

import type { ComboboxFieldOption, ComboboxFieldValue } from './ValueSelect/types'

export type TimezoneSelectProps = {
    id?: string
    className?: string
    disabled?: boolean
    required?: boolean
    'aria-invalid'?: boolean
    value: Timezone
    onChange: (timezone: Timezone) => void
}

const timezoneOptions: ComboboxFieldOption[] = validTimezones.map((timezone) => ({
    label: timezone,
    value: timezone,
}))

const normalizeTimezoneSearchValue = (value: string) =>
    value.toLowerCase().replace(/[/_]+/g, ' ').replace(/\s+/g, ' ').trim()

const filterTimezoneOption = (option: ComboboxFieldOption, query: string) =>
    normalizeTimezoneSearchValue(option.label).includes(normalizeTimezoneSearchValue(query))

export const TimezoneSelect = ({
    id,
    className,
    disabled,
    required,
    'aria-invalid': ariaInvalid,
    value,
    onChange,
}: TimezoneSelectProps) => {
    const { t } = useTranslation()

    const handleChange = useCallback(
        (newValue: ComboboxFieldValue) => {
            const result = TimezoneSchema.safeParse(newValue)
            if (result.success) {
                onChange(result.data)
            }
        },
        [onChange]
    )

    const searchPlaceholder = t('components.control.searchInput.placeholder')

    return (
        <ValueSelect
            aria-invalid={ariaInvalid}
            buttonProps={{
                id,
                className: cn('w-full', className),
                size: 'default',
                variant: 'outline',
            }}
            disabled={disabled}
            filter={filterTimezoneOption}
            onChange={handleChange}
            options={timezoneOptions}
            placeholder={searchPlaceholder}
            required={required}
            searchPlaceholder={searchPlaceholder}
            showChevron
            type="selector"
            value={value}
            virtualized
        />
    )
}
